/**
 * Shared REST client for the 606 Fitness admin bridge.
 *
 * All calls go through the ASB606 backend proxy at
 *     /api/f606/*
 * which forwards them to the real bridge on the 606 Fitness app.
 *
 * This keeps the integration key secret (only stored server-side) and
 * lets us swap providers/URLs without touching the frontend.
 */
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Wraps a bridge call.
 *   • Adds admin JWT + Idempotency-Key.
 *   • Normalises the response envelope to { success, data, error, actions_fired }.
 *   • Returns the whole envelope so the caller can inspect actions_fired.
 */
async function bridgeCall(method, path, body = null, params = null) {
  const idem = uuidv4();
  const headers = { ...getAuthHeaders(), 'Idempotency-Key': idem };
  try {
    const res = await axios({
      method,
      url: `${API_URL}/api/f606${path}`,
      headers,
      data: body,
      params,
    });
    // Bridge already emits { success, data, error, actions_fired }
    return res.data;
  } catch (err) {
    // Bridge errors arrive double-wrapped: proxy HTTPException({detail: bridgeBody})
    // where bridgeBody itself may be {detail: envelope}. Unwrap until we find
    // the real { success, error } envelope.
    let payload = err?.response?.data;
    while (payload && typeof payload === 'object' && payload.detail !== undefined && payload.error === undefined) {
      payload = payload.detail;
    }
    if (payload?.error) return { success: false, actions_fired: [], data: null, ...payload };
    if (typeof payload === 'string') {
      return { success: false, error: { code: 'bridge_error', message: payload }, data: null, actions_fired: [] };
    }
    return {
      success: false,
      error: {
        code: 'network_error',
        message: err.message || 'Network error',
      },
      data: null,
      actions_fired: [],
    };
  }
}

export const bridge = {
  // ── Read ──────────────────────────────────────────
  list: (entity, params) => bridgeCall('get', `/${entity}`, null, params),
  stats: () => bridgeCall('get', '/stats/overview'),
  alerts: () => bridgeCall('get', '/alerts'),

  // ── Orders ────────────────────────────────────────
  order: {
    confirm: (id, body = {}) => bridgeCall('put',  `/orders/${id}/confirm`, body),
    ship:    (id, body = {}) => bridgeCall('put',  `/orders/${id}/ship`, body),
    deliver: (id, body = {}) => bridgeCall('put',  `/orders/${id}/deliver`, body),
    cancel:  (id, body = {}) => bridgeCall('put',  `/orders/${id}/cancel`, body),
    addNote: (id, body)      => bridgeCall('post', `/orders/${id}/notes`, body),
  },

  // ── Users ─────────────────────────────────────────
  user: {
    ban:              (id, body = {}) => bridgeCall('post',   `/users/${id}/ban`, body),
    unban:            (id)            => bridgeCall('post',   `/users/${id}/unban`),
    notify:           (id, body)      => bridgeCall('post',   `/users/${id}/notify`, body),
    giftSubscription: (id, body)      => bridgeCall('post',   `/users/${id}/gift-subscription`, body),
  },

  // ── Subscriptions ─────────────────────────────────
  subscription: {
    extend:  (id, body) => bridgeCall('put',  `/subscriptions/${id}/extend`, body),
    cancel:  (id, body) => bridgeCall('put',  `/subscriptions/${id}/cancel`, body),
    upgrade: (id, body) => bridgeCall('post', `/subscriptions/${id}/upgrade`, body),
  },

  // ── Trainers ──────────────────────────────────────
  trainer: {
    verify:   (id, body = {}) => bridgeCall('post', `/trainers/${id}/verify`, body),
    unverify: (id, body = {}) => bridgeCall('post', `/trainers/${id}/unverify`, body),
    payout:   (id, body)      => bridgeCall('post', `/trainers/${id}/payout`, body),
  },

  // ── Products ──────────────────────────────────────
  product: {
    update: (id, body)      => bridgeCall('put',  `/products/${id}`, body),
    hide:   (id)            => bridgeCall('post', `/products/${id}/hide`),
    unhide: (id)            => bridgeCall('post', `/products/${id}/unhide`),
  },

  // ── Announcements ─────────────────────────────────
  announcement: {
    broadcast: (body) => bridgeCall('post', '/announcements/broadcast', body),
    status:    (jobId) => bridgeCall('get',  `/announcements/${jobId}/status`),
  },

  // ── Command queue ─────────────────────────────────
  commands: {
    list: (status = null) => bridgeCall('get', '/commands', null, status ? { status } : null),
    get:  (id) => bridgeCall('get',  `/commands/${id}`),
    add:  (body) => bridgeCall('post', '/commands', body),
  },
};

/**
 * Nicer toast helper: shows both the outcome AND which side-effects
 * fired (email/push/webhook) so the operator sees what really happened.
 * Push and SMS are still MOCKED on the bridge side, so we hint that.
 */
export function describeActions(actions = [], language = 'ar') {
  if (!actions?.length) return '';
  const isAr = language === 'ar';
  const map = isAr ? {
    status_updated:   'تحديث الحالة',
    email_sent:       'بريد',
    push_sent:        'push (طابور 🟡)',
    push_queued:      'push (طابور 🟡)',
    sms_sent:         'SMS (طابور 🟡)',
    sms_queued:       'SMS (طابور 🟡)',
    webhook_fired:    'webhook',
    webhook_delivered:'webhook',
    audit_logged:     'سجلّ',
    notification_created: 'إشعار داخل التطبيق',
  } : {
    status_updated:   'status',
    email_sent:       'email',
    push_sent:        'push (queued 🟡)',
    push_queued:      'push (queued 🟡)',
    sms_sent:         'sms (queued 🟡)',
    sms_queued:       'sms (queued 🟡)',
    webhook_fired:    'webhook',
    webhook_delivered:'webhook',
    audit_logged:     'audit',
    notification_created: 'in-app',
  };
  return actions.map((a) => map[a] || a).join(' · ');
}
